import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../authService';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  TextField, 
  Typography, 
  Alert, 
  IconButton, 
  InputAdornment, 
  CircularProgress, 
  Avatar, 
  Stack, 
  Divider, 
  Fade,
  Chip
} from '@mui/material';
import { 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff, 
  AccountBalance, 
  ShieldOutlined,
  CheckCircle,
  WaterDrop,
  Receipt,
  Info,
  ArrowBack
} from '@mui/icons-material';
import MobileBottomBar from './common/MobileBottomBar';

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Staff Sign In - LGU Concepcion Systems Portal';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!email || !password) {
      setError('Please enter both your email/username and password');
      setLoading(false);
      return;
    }

    try {
      const result = await authService.login(email, password);
      
      if (result.success) {
        authService.setUserSession(result.user);
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        navigate('/portal', { replace: true });
      } else {
        setError(result.error || 'Authentication failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected system error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        bgcolor: '#f8faf9', 
        px: { xs: 2, sm: 3 },
        py: 4,
        position: 'relative'
      }}
    >
      {/* Back to Home Button */}
      <Box sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{
            color: '#475569',
            fontWeight: 600,
            borderRadius: 2,
            px: 2,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            border: '1px solid #e2e8f0',
            '&:hover': {
              bgcolor: '#ffffff',
              borderColor: '#cbd5e1'
            }
          }}
        >
          Back to Portal Home
        </Button>
      </Box>

      <Container maxWidth="md" sx={{ pb: { xs: 8, md: 0 } }}>
        <Fade in={true} timeout={500}>
          <Paper 
            elevation={0}
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              overflow: 'hidden', 
              borderRadius: 4, 
              border: '1px solid #e2e8f0',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)' 
            }}
          >
            {/* Form Section */}
            <Box sx={{ flex: 1.1, bgcolor: '#ffffff', p: { xs: 3.5, sm: 4.5 } }}>
              <Stack spacing={3}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1.25} mb={1}>
                    <Box 
                      sx={{ 
                        width: 38, 
                        height: 38, 
                        borderRadius: 2.5, 
                        bgcolor: 'rgba(5, 150, 105, 0.12)', 
                        color: '#059669', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={800} color="#0f172a">
                      LGU Concepcion
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter authorized staff credentials to access the Online Systems Portal & ERP subsystems.
                  </Typography>
                </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    onClose={() => setError('')} 
                    sx={{ 
                      borderRadius: 2.5, 
                      fontSize: '0.8125rem',
                      fontWeight: 500
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Stack spacing={2.25}>
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="#334155" sx={{ display: 'block', mb: 0.75 }}>
                        EMAIL ADDRESS OR USERNAME
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. staff@concepcion.gov.ph"
                        variant="outlined"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: '#94a3b8', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2.5, bgcolor: '#ffffff' }
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption" fontWeight={700} color="#334155" sx={{ display: 'block', mb: 0.75 }}>
                        PASSWORD
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter your security password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: '#94a3b8', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2.5, bgcolor: '#ffffff' }
                        }}
                      />
                    </Box>

                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{
                        mt: 1,
                        py: 1.35,
                        fontSize: '0.9375rem',
                        fontWeight: 700,
                        borderRadius: 2.5,
                        bgcolor: '#059669',
                        color: '#ffffff',
                        boxShadow: '0 4px 14px rgba(5, 150, 105, 0.3)',
                        '&:hover': {
                          bgcolor: '#047857',
                          boxShadow: '0 6px 18px rgba(5, 150, 105, 0.4)'
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In to Portal'}
                    </Button>
                  </Stack>
                </form>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <ShieldOutlined sx={{ fontSize: 16, color: '#059669' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Encrypted Municipal Session
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.disabled">
                    v2.0 Enterprise
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Brand / Information Banner */}
            <Box 
              sx={{ 
                flex: 0.9, 
                display: { xs: 'none', md: 'flex' }, 
                flexDirection: 'column',
                justifyContent: 'space-between',
                bgcolor: '#047857', 
                background: 'linear-gradient(145deg, #059669 0%, #047857 60%, #064e3b 100%)',
                color: '#ffffff', 
                p: 4.5,
                position: 'relative'
              }}
            >
              <Box>
                <Chip 
                  label="Official Staff Access" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)', 
                    color: '#ffffff', 
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    mb: 3
                  }} 
                />
                <Typography variant="h4" fontWeight={800} sx={{ color: '#ffffff', lineHeight: 1.2, mb: 1.5 }}>
                  Unified Municipal ERP Platform
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, mb: 4 }}>
                  Seamless integration for Municipal Treasury, Water Billing, Real Property Tax (RPT), BPLO Licensing, Rural Health, and e-LogBook.
                </Typography>

                <Stack spacing={1.5}>
                  {[
                    'Role-based access control & audit trails',
                    'Realtime staff collaboration & notifications',
                    'Automated BIR & Form 56 receipts generation'
                  ].map((feat, idx) => (
                    <Box key={idx} display="flex" alignItems="center" gap={1}>
                      <CheckCircle sx={{ fontSize: 16, color: '#a7f3d0' }} />
                      <Typography variant="caption" sx={{ color: '#ecfdf5', fontWeight: 500 }}>
                        {feat}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Typography variant="caption" sx={{ opacity: 0.7, mt: 4, display: 'block' }}>
                © {new Date().getFullYear()} LGU Concepcion, Romblon.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={[
          { name: 'Home', icon: <Info />, path: '/' },
          { name: 'Water Bills', icon: <WaterDrop />, path: '/check-water-bills' },
          { name: 'Unclaimed Checks', icon: <Receipt />, path: '/check-unclaimed-checks' },
          { name: 'Sign In', icon: <Lock />, path: '/login' }
        ]}
        currentPath="/login"
        onNavigate={(path) => navigate(path)}
      />
    </Box>
  );
};

export default Login;

